// ===============================================================
// QUIZZES & REFLEXIONES · Karina Negrín · CJPPU 145864
// ===============================================================
//
// Este archivo contiene 4 quizzes:
//
//  1. EPDS — Escala de Depresión Posnatal de Edimburgo (VALIDADO)
//     Cox, Holden & Sagovsky (1987) · Br J Psychiatry 150:782-786.
//     Validación en español: Vega-Dienstmaier et al.; Garcia-Esteve et al.
//
//  2. Crisis vitales — REFLEXIVO (no diagnóstico)
//  3. Estilo de apego de tu hij@ (1-6 años) — REFLEXIVO (no diagnóstico)
//  4. ¿Cómo estás viviendo tu maternidad? — REFLEXIVO (no diagnóstico)
//
// Para AGREGAR un quiz nuevo, copiá la estructura de uno existente
// y respetá los campos: id, type, title, intro, questions, results.
//
// type: 'validated' | 'reflective-category' | 'reflective-score'
// ===============================================================

export const quizzesIndex = [
  {
    id: 'epds',
    title: 'Cómo estoy emocionalmente después del parto',
    subtitle: 'Escala de Edimburgo (EPDS)',
    description:
      'Una herramienta breve y científicamente validada que ayuda a detectar señales de tristeza o ansiedad perinatal. No es un diagnóstico — es un primer paso para registrarte.',
    duration: '3 minutos',
    questionsCount: 10,
    badge: 'Test validado científicamente',
    forWho: 'Mujeres en embarazo o posparto (hasta 12 meses)',
    icon: 'HeartPulse',
  },
  {
    id: 'crisis-vital',
    title: '¿Qué momento de tu vida estás transitando?',
    subtitle: 'Una mirada a las crisis vitales femeninas',
    description:
      'No siempre las mujeres atravesamos los mismos momentos al mismo tiempo. Estas preguntas son una invitación a registrar dónde estás hoy — sin etiquetas, sin diagnósticos.',
    duration: '4 minutos',
    questionsCount: 8,
    badge: 'Reflexivo · no diagnóstico',
    forWho: 'Cualquier mujer',
    icon: 'Compass',
  },
  {
    id: 'apego-hijo',
    title: 'El estilo vincular de tu hij@',
    subtitle: 'Para mamás de niñ@s entre 1 y 6 años',
    description:
      'Inspirado en la teoría del apego (Bowlby, Ainsworth). No clasifica a tu hij@ ni lo diagnostica: te ofrece una mirada sobre cómo se va construyendo el vínculo entre ustedes.',
    duration: '4 minutos',
    questionsCount: 10,
    badge: 'Reflexivo · no diagnóstico',
    forWho: 'Mamás de niñ@s de 1 a 6 años',
    icon: 'Users',
  },
  {
    id: 'maternidad',
    title: '¿Cómo estás viviendo tu maternidad?',
    subtitle: 'Matrescencia, carga y registro propio',
    description:
      'Maternar transforma. Estas preguntas no miden ni juzgan: te ayudan a registrar cómo estás vos, hoy, en este tránsito. El primer paso para cuidarte es escucharte.',
    duration: '3 minutos',
    questionsCount: 8,
    badge: 'Reflexivo · no diagnóstico',
    forWho: 'Mamás en cualquier etapa',
    icon: 'Sparkles',
  },
];

// ===============================================================
// 1 · EPDS — Escala de Edimburgo (VALIDADA)
// ===============================================================

export const epds = {
  id: 'epds',
  type: 'validated',
  title: 'Escala de Edimburgo · EPDS',
  subtitle: 'Cómo me he sentido en los últimos 7 días',
  intro:
    'Te invito a marcar la respuesta que más se acerca a cómo te sentiste en los últimos 7 días, no solo cómo te sentís hoy. Pensá en la frecuencia o intensidad real, no en cómo "deberías" sentirte.',
  warning:
    'Esta escala es un screening: detecta señales, no realiza diagnósticos. El resultado es orientativo y nunca reemplaza una consulta clínica.',
  citation:
    'Adaptado de Cox, Holden & Sagovsky (1987). British Journal of Psychiatry, 150, 782-786. Versión en español validada en población latinoamericana.',
  questions: [
    {
      id: 'q1',
      text: 'En los últimos 7 días, he sido capaz de reír y ver el lado bueno de las cosas',
      options: [
        { label: 'Tanto como siempre', score: 0 },
        { label: 'No tanto ahora', score: 1 },
        { label: 'Mucho menos ahora', score: 2 },
        { label: 'No, nada', score: 3 },
      ],
    },
    {
      id: 'q2',
      text: 'He disfrutado mirar hacia adelante con ilusión',
      options: [
        { label: 'Tanto como siempre', score: 0 },
        { label: 'Algo menos de lo que solía', score: 1 },
        { label: 'Mucho menos de lo que solía', score: 2 },
        { label: 'Casi nada', score: 3 },
      ],
    },
    {
      id: 'q3',
      text: 'Me he culpado innecesariamente cuando las cosas iban mal',
      options: [
        { label: 'Sí, la mayor parte del tiempo', score: 3 },
        { label: 'Sí, a veces', score: 2 },
        { label: 'No con mucha frecuencia', score: 1 },
        { label: 'No, nunca', score: 0 },
      ],
    },
    {
      id: 'q4',
      text: 'He estado nerviosa o preocupada sin motivo',
      options: [
        { label: 'No, nunca', score: 0 },
        { label: 'Casi nunca', score: 1 },
        { label: 'Sí, a veces', score: 2 },
        { label: 'Sí, con mucha frecuencia', score: 3 },
      ],
    },
    {
      id: 'q5',
      text: 'He sentido miedo o pánico sin un motivo claro',
      options: [
        { label: 'Sí, bastante', score: 3 },
        { label: 'Sí, a veces', score: 2 },
        { label: 'No, no mucho', score: 1 },
        { label: 'No, nada', score: 0 },
      ],
    },
    {
      id: 'q6',
      text: 'Las cosas me han estado abrumando',
      options: [
        { label: 'Sí, la mayoría de las veces no he podido afrontarlas', score: 3 },
        { label: 'Sí, a veces no las he afrontado tan bien como antes', score: 2 },
        { label: 'No, la mayor parte del tiempo las he afrontado bastante bien', score: 1 },
        { label: 'No, las he estado afrontando tan bien como siempre', score: 0 },
      ],
    },
    {
      id: 'q7',
      text: 'Me he sentido tan infeliz que he tenido dificultades para dormir',
      options: [
        { label: 'Sí, la mayor parte del tiempo', score: 3 },
        { label: 'Sí, a veces', score: 2 },
        { label: 'No con mucha frecuencia', score: 1 },
        { label: 'No, nunca', score: 0 },
      ],
    },
    {
      id: 'q8',
      text: 'Me he sentido triste o desdichada',
      options: [
        { label: 'Sí, la mayor parte del tiempo', score: 3 },
        { label: 'Sí, bastante a menudo', score: 2 },
        { label: 'No con mucha frecuencia', score: 1 },
        { label: 'No, nunca', score: 0 },
      ],
    },
    {
      id: 'q9',
      text: 'Me he sentido tan infeliz que he estado llorando',
      options: [
        { label: 'Sí, la mayor parte del tiempo', score: 3 },
        { label: 'Sí, bastante a menudo', score: 2 },
        { label: 'Solo de vez en cuando', score: 1 },
        { label: 'No, nunca', score: 0 },
      ],
    },
    {
      id: 'q10',
      text: 'Se me ha ocurrido la idea de hacerme daño',
      sensitive: true,
      options: [
        { label: 'Sí, bastante a menudo', score: 3 },
        { label: 'A veces', score: 2 },
        { label: 'Casi nunca', score: 1 },
        { label: 'Nunca', score: 0 },
      ],
    },
  ],
  // results se calculan en runtime según score y respuesta a q10
  resultBands: [
    {
      min: 0,
      max: 9,
      title: 'Score bajo',
      headline: 'Las señales de malestar significativo, en este momento, parecen estar bajas.',
      body:
        'Esto no significa que esté "todo bien" —cada experiencia es única—, pero según esta escala no se detectan signos importantes de depresión o ansiedad perinatal en los últimos 7 días. Si igual sentís que algo te pesa, vale la pena hablarlo. La salud emocional no se trata solo de presencia de síntomas, sino también de cómo te sentís vos con tu vida.',
    },
    {
      min: 10,
      max: 12,
      title: 'Score intermedio',
      headline: 'Aparecen algunas señales que vale la pena registrar.',
      body:
        'Tu puntaje sugiere que en los últimos 7 días estuvieron presentes ciertos síntomas que podrían estar indicando un proceso de tristeza, ansiedad o sobrecarga emocional. No es un diagnóstico, pero sí una invitación clara a tomarte tu sentir en serio y conversarlo con un profesional. Cuanto antes se acompaña, más liviano y posible se vuelve el camino.',
    },
    {
      min: 13,
      max: 30,
      title: 'Score alto',
      headline:
        'El resultado sugiere que estás atravesando un malestar significativo. Lo importante es que no estás sola.',
      body:
        'Este puntaje indica una probable presencia de síntomas depresivos y/o ansiosos clínicamente relevantes. NO es un diagnóstico —solo un profesional puede establecerlo en una consulta— pero sí un llamado claro a buscar acompañamiento. La depresión perinatal es frecuente, tratable y nadie debería atravesarla sola. Te invito a contactarte conmigo o con un/a profesional de confianza lo antes posible.',
    },
  ],
  // Si q10 ≥ 1 (cualquier respuesta distinta a "Nunca")
  selfHarmAlert: {
    title: 'Una nota importante antes del resultado',
    body:
      'Marcaste que se te ha ocurrido la idea de hacerte daño, aunque sea muy poco. Quiero que sepas que eso es información valiosa que merece ser escuchada, sin juicio. Pensamientos así pueden aparecer en momentos de mucho dolor — y siempre es prioritario acompañarlos. Te pido que, además del resultado de esta escala, te contactes hoy mismo con alguien que pueda sostenerte.',
    resources: [
      {
        label: 'Línea Vida (Uruguay) · 0800 0767',
        href: 'tel:08000767',
        meta: 'Atención 24 hs, gratuita y confidencial',
      },
      {
        label: 'WhatsApp con Karina',
        href: 'https://wa.me/59898884967?text=Hola%20Karina%2C%20hice%20la%20escala%20de%20Edimburgo%20y%20quisiera%20hablar%20con%20vos',
        meta: 'Te respondo personalmente',
      },
    ],
  },
};

// ===============================================================
// 2 · CRISIS VITALES — REFLEXIVO
// ===============================================================

export const crisisVital = {
  id: 'crisis-vital',
  type: 'reflective-category',
  title: '¿Qué momento estás transitando?',
  subtitle: 'Una mirada a las crisis vitales femeninas',
  intro:
    'Las mujeres no atravesamos las mismas etapas al mismo tiempo. Este test no clasifica ni etiqueta — sólo te invita a registrar dónde estás hoy. Elegí lo que más se acerque, aunque sientas que ninguna opción es perfecta.',
  warning:
    'Esto NO es un diagnóstico ni una herramienta clínica validada. Es una propuesta reflexiva basada en literatura sobre transiciones vitales. No reemplaza una consulta profesional.',
  questions: [
    {
      id: 'q1',
      text: 'Cuando pienso en cómo llegué hasta acá, siento que…',
      options: [
        { label: 'Sé exactamente cómo y por qué', cat: 'plenitud' },
        { label: 'Algunas elecciones tienen sentido, otras me cuestan reconocer', cat: 'transito' },
        { label: 'Cumplí lo que se esperaba más que lo que quería', cat: 'encrucijada' },
        { label: 'No me reconozco del todo en mi vida actual', cat: 'pausa' },
      ],
    },
    {
      id: 'q2',
      text: 'Cuando miro hacia adelante…',
      options: [
        { label: 'Siento curiosidad y ganas', cat: 'plenitud' },
        { label: 'Necesito frenar antes de decidir nada', cat: 'transito' },
        { label: 'Aparece presión por no haber llegado donde "debería"', cat: 'encrucijada' },
        { label: 'Siento vértigo o un cansancio enorme', cat: 'pausa' },
      ],
    },
    {
      id: 'q3',
      text: 'Mi cuerpo, en este momento…',
      options: [
        { label: 'Me acompaña', cat: 'plenitud' },
        { label: 'Está cambiando y a veces no me reconozco', cat: 'transito' },
        { label: 'Me siento desconectada de él', cat: 'encrucijada' },
        { label: 'Me grita cosas que no termino de escuchar', cat: 'pausa' },
      ],
    },
    {
      id: 'q4',
      text: 'Mis vínculos cercanos…',
      options: [
        { label: 'Me sostienen', cat: 'plenitud' },
        { label: 'Algunos los quiero soltar o transformar', cat: 'transito' },
        { label: 'Sostienen ellos a mí, y eso pesa', cat: 'encrucijada' },
        { label: 'Necesito estar más sola', cat: 'pausa' },
      ],
    },
    {
      id: 'q5',
      text: 'Lo que más necesito hoy es…',
      options: [
        { label: 'Seguir construyendo lo que ya tengo', cat: 'plenitud' },
        { label: 'Permiso para no saber todavía', cat: 'transito' },
        { label: 'Coraje para cambiar algo', cat: 'encrucijada' },
        { label: 'Descanso real, profundo', cat: 'pausa' },
      ],
    },
    {
      id: 'q6',
      text: 'Si pudieras tomarte un año libre…',
      options: [
        { label: 'No lo necesito ahora', cat: 'plenitud' },
        { label: 'A veces lo fantaseo', cat: 'transito' },
        { label: 'No sé qué haría con tanto tiempo', cat: 'encrucijada' },
        { label: 'Lo necesito urgente', cat: 'pausa' },
      ],
    },
    {
      id: 'q7',
      text: 'Cuando estás sola con vos misma…',
      options: [
        { label: 'Me siento bien conmigo', cat: 'plenitud' },
        { label: 'Es donde mejor pienso', cat: 'transito' },
        { label: 'Aparece algo incómodo que prefiero esquivar', cat: 'encrucijada' },
        { label: 'Me angustia un poco', cat: 'pausa' },
      ],
    },
    {
      id: 'q8',
      text: 'Si tuvieras que describir este momento de tu vida en una palabra…',
      options: [
        { label: 'Plenitud', cat: 'plenitud' },
        { label: 'Movimiento', cat: 'transito' },
        { label: 'Encrucijada', cat: 'encrucijada' },
        { label: 'Pausa forzada', cat: 'pausa' },
      ],
    },
  ],
  results: {
    plenitud: {
      title: 'Momento de plenitud y consolidación',
      headline:
        'Hay algo de tu presente que se siente alineado con lo que sos y querés. Disfrutá esto sin culpa.',
      body:
        'No siempre la vida está en crisis. A veces transitamos momentos de cierta paz, claridad y disfrute. Si te pasa eso, no lo subestimes ni lo escondas: registrarlo es parte del cuidado. Estos momentos también son frágiles y vale la pena cuidarlos cultivando lo que te sostiene: vínculos, descanso, propósito. Si igual sentís alguna sombra debajo, también podemos mirarla.',
      practices: [
        'Anotá una vez por semana qué te sostiene en este momento, para tener registro propio.',
        'Cuidá los vínculos que te nutren — no demos por sentado lo que está bien.',
        'Si aparece culpa por estar bien, recordá: estar bien también es válido y revolucionario.',
      ],
    },
    transito: {
      title: 'Momento de tránsito y replanteo',
      headline:
        'Estás en movimiento. Algunas cosas se están acomodando, otras se están soltando.',
      body:
        'Los tránsitos no son fracasos: son la forma en que la vida se reescribe. Tal vez sientas que ya no encajás del todo donde estabas, pero todavía no sabés del todo hacia dónde vas. Esa zona "intermedia" es incómoda y a veces da miedo, pero es donde se gesta lo nuevo. No te apures a tener todas las respuestas. Permitirte el "no saber" es parte del proceso.',
      practices: [
        'Hacé una lista de lo que ya no te cierra, sin obligarte todavía a soltarlo.',
        'Registrar pequeños cambios cotidianos — los tránsitos se notan en lo chico.',
        'Conversar este momento con alguien de confianza ayuda a poner palabras donde solo hay sensación.',
      ],
    },
    encrucijada: {
      title: 'Momento de encrucijada',
      headline:
        'Sentís una tensión entre lo que estás viviendo y lo que querrías estar viviendo.',
      body:
        'Las encrucijadas aparecen cuando algo de adentro pide cambio pero algo de afuera (o de adentro también) sostiene lo que ya está. Es un lugar de mucha información: si te animás a escucharte, hay decisiones importantes esperando. No tenés que resolverlas hoy ni sola. Acompañarte profesionalmente en este momento puede ayudarte a clarificar qué es lo tuyo y qué son mandatos.',
      practices: [
        'Identificar UN tema central donde sentís la tensión (trabajo, vínculos, cuerpo, maternidad).',
        'Escribir sin censura qué te gustaría si nadie te juzgara.',
        'Considerar acompañamiento terapéutico — no para "decidir por vos", sino para escucharte mejor.',
      ],
    },
    pausa: {
      title: 'Pausa forzada · cansancio profundo',
      headline:
        'Tu cuerpo y tu mente te están pidiendo un freno real. Eso merece ser escuchado.',
      body:
        'Hay un agotamiento que no se cura con un fin de semana — es ese cansancio que se acumuló por meses o años de sostener mucho. Si aparece angustia, desconexión, ganas de "desaparecer un rato", no es debilidad: es una señal de tu sistema diciendo que necesita cuidado serio. Te recomiendo no atravesar esto sola. Hablar con un/a profesional puede ayudarte a empezar a ordenar lo que está saturado.',
      practices: [
        'Reducir lo que sea reducible esta semana — no es egoísmo, es supervivencia.',
        'Pedir ayuda concreta a una persona específica (no genérico, algo puntual).',
        'Considerar consulta psicológica para no atravesar este momento sin acompañamiento.',
      ],
    },
  },
};

// ===============================================================
// 3 · ESTILO DE APEGO DE TU HIJ@ — REFLEXIVO (1-6 años)
// ===============================================================

export const apegoHijo = {
  id: 'apego-hijo',
  type: 'reflective-category',
  title: 'El estilo vincular de tu hij@',
  subtitle: 'Una mirada inspirada en la teoría del apego',
  intro:
    'Pensá en tu hij@ entre 1 y 6 años. Elegí la opción que más se acerque a lo que sueles observar — aunque ningún niñ@ encaja exacto en una categoría. Si te cuesta decidir, elegí la más frecuente.',
  warning:
    'Esto NO clasifica ni diagnostica a tu hij@. La teoría del apego es compleja y siempre se evalúa clínicamente con observación profesional. Es una invitación a pensar el vínculo, no una etiqueta.',
  citation:
    'Inspirado en los aportes de Bowlby (1969) y Ainsworth (1978). Las descripciones son orientativas, no clínicas.',
  questions: [
    {
      id: 'q1',
      text: 'Cuando te vas (al jardín, al trabajo) y volvés…',
      options: [
        { label: 'Se enoja un rato pero después se acomoda', cat: 'seguro' },
        { label: 'Parece no notarlo demasiado', cat: 'evitativo' },
        { label: 'Se aferra mucho y le cuesta calmarse', cat: 'ansioso' },
        { label: 'Reacciona de formas muy distintas según el día', cat: 'desorganizado' },
      ],
    },
    {
      id: 'q2',
      text: 'Cuando se cae o se asusta…',
      options: [
        { label: 'Te busca, se calma con vos y vuelve a jugar', cat: 'seguro' },
        { label: 'Llora pero solo, evita el consuelo', cat: 'evitativo' },
        { label: 'Llora muchísimo y le cuesta calmarse aún con vos', cat: 'ansioso' },
        { label: 'A veces te busca, a veces te rechaza', cat: 'desorganizado' },
      ],
    },
    {
      id: 'q3',
      text: 'Frente a personas nuevas…',
      options: [
        { label: 'Se acerca con cautela y termina interactuando', cat: 'seguro' },
        { label: 'Las ignora o se desentiende', cat: 'evitativo' },
        { label: 'Se aferra a vos y no quiere participar', cat: 'ansioso' },
        { label: 'Reacciona de formas muy variables', cat: 'desorganizado' },
      ],
    },
    {
      id: 'q4',
      text: 'Cuando juega…',
      options: [
        { label: 'Explora y vuelve a vos cada tanto a "cargarse"', cat: 'seguro' },
        { label: 'Juega solo, no busca compartir mucho', cat: 'evitativo' },
        { label: 'Necesita que estés todo el tiempo cerca', cat: 'ansioso' },
        { label: 'Su juego cambia bruscamente, sin patrón claro', cat: 'desorganizado' },
      ],
    },
    {
      id: 'q5',
      text: 'Cuando lo retás o ponés un límite…',
      options: [
        { label: 'Se enoja pero después entiende y se reconecta', cat: 'seguro' },
        { label: 'No reacciona, se cierra', cat: 'evitativo' },
        { label: 'Se desborda emocionalmente', cat: 'ansioso' },
        { label: 'Las reacciones son impredecibles', cat: 'desorganizado' },
      ],
    },
    {
      id: 'q6',
      text: 'En la forma de pedir contacto físico (upa, abrazo)…',
      options: [
        { label: 'Lo pide cuando lo necesita, sin obsesión', cat: 'seguro' },
        { label: 'Casi no pide, parece no necesitarlo', cat: 'evitativo' },
        { label: 'Lo pide constantemente', cat: 'ansioso' },
        { label: 'Lo pide y rechaza al mismo tiempo', cat: 'desorganizado' },
      ],
    },
    {
      id: 'q7',
      text: 'Sus emociones, en general…',
      options: [
        { label: 'Las muestra de forma proporcional al estímulo', cat: 'seguro' },
        { label: 'Casi no las muestra', cat: 'evitativo' },
        { label: 'Las muestra muy intensamente', cat: 'ansioso' },
        { label: 'Cambia de emoción muy rápido y sin transición', cat: 'desorganizado' },
      ],
    },
    {
      id: 'q8',
      text: 'A la hora de dormir…',
      options: [
        { label: 'Se duerme tranquilo si estás cerca', cat: 'seguro' },
        { label: 'Se duerme solo, no le importa la cercanía', cat: 'evitativo' },
        { label: 'No se duerme sin contacto físico continuo', cat: 'ansioso' },
        { label: 'Le cuesta dormir, se despierta angustiado', cat: 'desorganizado' },
      ],
    },
    {
      id: 'q9',
      text: 'Si pierde un objeto querido (peluche, juguete favorito)…',
      options: [
        { label: 'Se entristece y busca consuelo en vos', cat: 'seguro' },
        { label: 'No parece afectarle mucho', cat: 'evitativo' },
        { label: 'Se desespera de forma intensa', cat: 'ansioso' },
        { label: 'Su reacción es difícil de leer', cat: 'desorganizado' },
      ],
    },
    {
      id: 'q10',
      text: 'Cuando vos estás triste o cansada…',
      options: [
        { label: 'Te ofrece consuelo o se acerca con suavidad', cat: 'seguro' },
        { label: 'Sigue como si nada', cat: 'evitativo' },
        { label: 'Se angustia y "te carga"', cat: 'ansioso' },
        { label: 'Reacciona de formas que cambian', cat: 'desorganizado' },
      ],
    },
  ],
  results: {
    seguro: {
      title: 'Patrón mayoritariamente seguro',
      headline:
        'Tu hij@ confía en que estás disponible. Eso no es un mérito puntual: es algo que se construye día a día.',
      body:
        'En el patrón seguro, l@s niñ@s pueden explorar el mundo porque saben que hay alguien a quien volver. No significa que no haya enojos, llantos o conflictos — significa que después de la tormenta, hay reparación. Esto se construye con presencia (no perfección) y con la capacidad adulta de regular las propias emociones para acompañar las de tu hij@. Felicitaciones por el trabajo invisible que hacés para sostener este vínculo.',
      practices: [
        'Sostené las pequeñas reparaciones cotidianas: "perdón por gritarte", "te entiendo aunque no te dé permiso ahora".',
        'Cuidá tu propio bienestar: el apego seguro se sostiene en adultos disponibles emocionalmente.',
        'Si aparecen momentos de mucha demanda, recordá: pedir ayuda no rompe el apego, lo cuida.',
      ],
    },
    evitativo: {
      title: 'Tendencia evitativa',
      headline:
        'Tu hij@ aprendió a regular sus emociones por su cuenta — eso no es bueno ni malo en sí mismo, pero merece mirada.',
      body:
        'Esta tendencia suele aparecer cuando el entorno fue, en algún momento, poco disponible para acompañar emociones difíciles. No implica falta de amor — muchas veces los adultos sostienen lo material y no llegan a lo emocional, o repiten cómo los criaron a ellos. La buena noticia: el patrón puede transformarse. Pequeños gestos de apertura emocional en lo cotidiano (poner palabras a lo que sentís, validar lo que siente él/ella) suman muchísimo.',
      practices: [
        'Nombrar emociones en voz alta, las tuyas y las suyas, sin esperar que las "muestre".',
        'Crear momentos de cercanía sin propósito: jugar, leer, mirarse. La conexión no siempre es verbal.',
        'Mirarte a vos: ¿hay emociones que te cuesta sentir? Eso modela.',
      ],
    },
    ansioso: {
      title: 'Tendencia ansiosa o ambivalente',
      headline:
        'Tu hij@ pide mucho contacto y aún así parece costarle sentirse seguro/a. Hay algo que vale la pena mirar.',
      body:
        'En esta tendencia los niñ@s muestran hipervigilancia hacia la figura de cuidado: la buscan mucho, pero también les cuesta calmarse cuando la tienen cerca. Suele aparecer cuando la disponibilidad adulta fue inconsistente (por estrés, por cambios, por agotamiento — todas razones humanas). No es culpa de nadie: es información. Trabajar la regularidad, las rutinas predecibles y bajar la propia carga emocional ayudan muchísimo.',
      practices: [
        'Sostener rutinas predecibles: la previsibilidad calma el sistema nervioso infantil.',
        'Cuidar tu propia regulación primero: tu calma es contagiosa, tu ansiedad también.',
        'Considerá acompañamiento profesional: a veces el patrón se ablanda con apoyo terapéutico vincular.',
      ],
    },
    desorganizado: {
      title: 'Señales de desorganización vincular',
      headline:
        'Aparecen señales contradictorias en cómo tu hij@ vive el vínculo. Es importante que esto no quede sin acompañamiento.',
      body:
        'El patrón desorganizado suele aparecer cuando el entorno generó miedo o impredecibilidad significativa — y muchas veces esto NO es culpa visible de nadie: puede asociarse a duelos, separaciones, hospitalizaciones, situaciones de alta tensión, traumas perinatales, depresión materna no tratada, etc. Lo importante es saber que se puede acompañar y modificar, especialmente entre 1 y 6 años. Te invito muy especialmente a buscar consulta profesional para evaluar y acompañar este vínculo con cuidado.',
      practices: [
        'Buscar consulta clínica vincular en la primera infancia (psicoterapia padres-hij@s).',
        'Cuidar tu propio proceso emocional: muchas veces "lo que el niñ@ muestra" se vincula con cosas que están atravesando los adultos.',
        'No interpretar las reacciones como "manipulación" o "capricho": son señales de un sistema desbordado.',
      ],
      urgentTone: true,
    },
  },
};

// ===============================================================
// 4 · MATERNIDAD — REFLEXIVO (con score)
// ===============================================================

export const maternidad = {
  id: 'maternidad',
  type: 'reflective-score',
  title: '¿Cómo estás viviendo tu maternidad?',
  subtitle: 'Matrescencia, carga y registro propio',
  intro:
    'Pensá en estas últimas semanas. Marcá la frecuencia que mejor describa lo que sentís. No hay respuestas correctas — esto es un espacio para registrarte vos.',
  warning:
    'Esto NO es un test diagnóstico ni un instrumento clínico. Es una herramienta de auto-observación con base en literatura sobre matrescencia (concepto desarrollado por Dana Raphael y profundizado por Aurélie Athan).',
  scale: [
    { label: 'Casi nunca', score: 0 },
    { label: 'A veces', score: 1 },
    { label: 'Bastante seguido', score: 2 },
    { label: 'Casi siempre', score: 3 },
  ],
  // inverse: true significa que invertir el score (pregunta positiva)
  questions: [
    { id: 'q1', text: 'Me reconozco poco de quien era antes de ser mamá', inverse: false },
    { id: 'q2', text: 'Siento que estoy haciendo todo, y aún así no me alcanza', inverse: false },
    { id: 'q3', text: 'Necesito un rato sola y no logro conseguirlo', inverse: false },
    { id: 'q4', text: 'Mi cuerpo me pide cosas que no puedo darme (descanso, atención, silencio)', inverse: false },
    { id: 'q5', text: 'Disfruto momentos con mi hij@', inverse: true },
    { id: 'q6', text: 'Me siento juzgada por la forma en que materno', inverse: false },
    { id: 'q7', text: 'Tengo permiso para pedir ayuda y la pido cuando la necesito', inverse: true },
    { id: 'q8', text: 'Estoy reencontrándome con formas nuevas de ser yo', inverse: true },
  ],
  // total: 0 a 24. Más alto = más sobrecarga.
  resultBands: [
    {
      min: 0,
      max: 8,
      title: 'Momento de reconexión',
      headline:
        'Estás registrando tu maternidad con conciencia y, en general, con espacio para vos.',
      body:
        'Tu respuestas sugieren que en este momento estás logrando integrar la maternidad con un cierto cuidado propio: hay disfrute, hay pedido de ayuda, hay reencuentros con vos. Eso no significa que sea fácil — significa que hay recursos en juego. Cuidalos: no se sostienen solos. Si aparece algún tema puntual que te molesta, podemos mirarlo igual.',
      practices: [
        'Sostené los rituales de cuidado propio que ya estás haciendo, aunque parezcan chicos.',
        'Anotá una vez a la semana qué te sostiene en este momento.',
        'No te exijas estar siempre así: la matrescencia tiene sus altibajos.',
      ],
    },
    {
      min: 9,
      max: 16,
      title: 'Maternidad atravesándote con intensidad',
      headline:
        'Tu maternidad se está sintiendo intensa. Hay carga, y también hay registro propio. Vale la pena acompañarte.',
      body:
        'Tus respuestas muestran un nivel medio de sobrecarga — algo bastante esperable, especialmente en etapas tempranas o en momentos de mucho cambio. Lo importante es que estés registrando lo que sentís y no minimizándolo. Ese registro es información valiosa. A veces, agregar una pequeña pausa real (no ocho cosas pendientes en 10 minutos) cambia todo. Considerar un espacio terapéutico puede ser un gran sostén.',
      practices: [
        'Identificar UNA cosa que vas a soltar esta semana, aunque sea chica.',
        'Pedir ayuda concreta a una persona específica: no genérica, algo puntual.',
        'Sumarte a un espacio grupal o terapéutico — la maternidad no es para atravesarla sola.',
      ],
    },
    {
      min: 17,
      max: 24,
      title: 'Estás cargando mucho',
      headline:
        'Tus respuestas reflejan un nivel alto de sobrecarga sostenida. Mereces acompañamiento real, no más exigencias.',
      body:
        'Lo que mostrás en este registro es un nivel de carga que excede lo que es saludable sostener sola. No es debilidad ni falla personal — es información clarísima de que tu sistema necesita más sostén del que está recibiendo. La matrescencia transforma profundamente, y cuando el entorno no acompaña suficiente, se vuelve agotamiento crónico, o algo más. Te invito muy especialmente a buscar consulta psicológica. No para "arreglarte" — para que dejes de cargar sola.',
      practices: [
        'Buscar consulta psicológica perinatal o de mujeres en cuanto puedas.',
        'Identificar UNA persona del entorno que pueda sostener tareas concretas esta semana.',
        'Frenar las auto-exigencias: tu prioridad ahora es no romperte.',
      ],
      urgentTone: true,
    },
  ],
};

// ===============================================================
// EXPORT — todos juntos
// ===============================================================

export const allQuizzes = {
  epds,
  'crisis-vital': crisisVital,
  'apego-hijo': apegoHijo,
  maternidad,
};

export const PROFESSIONAL_SIGN = {
  name: 'Lic. Karina Negrín',
  matricula: 'CJPPU 145864',
  role: 'Psicóloga · Diplomada en Psicología Perinatal',
};
